import { readonly, ref } from "vue";

/**
 * Which of the two recoverable states the server reported. Mirrors the codes in
 * `vpoc/vpoc/views.py`, which key off whether the session still identifies a user:
 * a stale token means the user is still logged in and only needs a fresh page.
 */
export const SESSION_EXPIRED = "session_expired";
export const CSRF_STALE_TOKEN = "csrf_stale_token";

export type SessionEndedCode = typeof SESSION_EXPIRED | typeof CSRF_STALE_TOKEN;

export type SessionEndedState = {
    code: SessionEndedCode;
    /** Where the dialog's button sends a signed-out user. Ignored for a stale token. */
    loginUrl: string | null;
};

const state = ref<SessionEndedState | null>(null);
let pageIsPublic = false;

/** Read by the single dialog mounted at the app root in vue.js. */
export const sessionEndedState = readonly(state);

/**
 * Declare that this page is served to visitors who were never signed in, opting it out of the
 * session-ended dialog. Call once from the page component.
 *
 * An external 360 rater is entitled by their invite token and has no account at all, so a
 * rejected token or a 403 there cannot be resolved by logging in.
 */
export function markPagePublic(): void {
    pageIsPublic = true;
}

/**
 * Drop the opt-out again, for a page that can hand over to an authenticated one without a full
 * page load.
 *
 * The flag is module state, so an Inertia visit -- which swaps the component but keeps the
 * module -- would otherwise carry a public page's opt-out onto the authenticated page it
 * navigated to, and silently swallow a real session expiry there. A page that opts in during
 * setup should pair this with `onUnmounted`.
 */
export function unmarkPagePublic(): void {
    pageIsPublic = false;
}

/** Whether the current page opted out of the session-ended dialog. */
export function isPagePublic(): boolean {
    return pageIsPublic;
}

/**
 * Open the session-ended dialog.
 *
 * First report wins. A dead session usually fails several in-flight requests at once, and the
 * first of them is as informative as the fifth; letting a later one overwrite the state would
 * swap the dialog's copy out from under someone mid-read.
 *
 * The one exception is an expiry arriving while the stale-token dialog is open: the user was
 * signed in when it opened and no longer is, so the dialog has to hide the page and discard
 * history like any other expiry (BUG-1125). Never the other way round.
 */
export function reportSessionEnded(code: SessionEndedCode, loginUrl: string | null = null): void {
    if (pageIsPublic) {
        return;
    }
    const promotesStaleToken = code === SESSION_EXPIRED && state.value?.code === CSRF_STALE_TOKEN;
    if (state.value && !promotesStaleToken) {
        return;
    }
    state.value = { code, loginUrl };
}

/**
 * Responses the fetch wrapper saw, could not recover, and deliberately chose NOT to report.
 *
 * Only the "refresh unavailable" case is marked: a 5xx or a dropped connection on the token
 * refresh says nothing about whether the session is alive, so opening the non-dismissible dialog
 * would strand a still-authenticated user and cost them anything unsaved on the reload.
 *
 * The mark exists because that decision has to survive the trip back out through vueuse.
 * `useFetch` hands the very same Response to `onFetchError` in `~vue/api`, whose backstop sees
 * our `code` in the parsed body and would report it anyway, undoing the choice one layer up.
 * Ownership is one-way: sessionEndedFetch decides, the backstop defers.
 *
 * Lives here rather than in sessionEndedFetch.ts because that module imports `~vue/api`, so
 * housing it there would make the backstop's import a cycle. Identity on the Response object is
 * what makes it work, and holds because the wrapper returns the rejected response unwrapped. A
 * WeakSet, so a response is collectable as soon as its caller drops it.
 */
let recoveryDeclined = new WeakSet<Response>();

/** Record that the wrapper handled this response and chose not to open the dialog for it. */
export function declineRecovery(response: Response): void {
    recoveryDeclined.add(response);
}

/** Whether the wrapper already decided not to report this response. */
export function wasRecoveryDeclined(response: Response | undefined | null): boolean {
    return response != null && recoveryDeclined.has(response);
}

/**
 * Whether a 403 body is the server saying the session or its CSRF token is done.
 *
 * Checks the code rather than the status: an ALB/WAF block is also a 403 (see wafDetection.ts),
 * and so is an ordinary permission denial, neither of which should offer a login prompt.
 */
export function sessionEndedCodeFrom(body: unknown): SessionEndedCode | null {
    if (!body || typeof body !== "object") {
        return null;
    }
    const code = (body as Record<string, unknown>).code;
    return code === SESSION_EXPIRED || code === CSRF_STALE_TOKEN ? code : null;
}

/**
 * Whether this 403 carries one of our session-ended codes, meaning the global fetch wrapper in
 * sessionEndedFetch.ts already refreshed the token, retried once, and (on failure) reported the
 * dialog. The legacy per-transport retries consult this and stand down: a further attempt could
 * land a write after the user was already told it failed.
 *
 * TODO: remove the legacy retries in StreamingPostClient and the chat document upload (and this
 * helper) once csrf_session_ended_codes is fully enabled and every CSRF 403 carries a code --
 * until then they are the only recovery those two paths have.
 */
export async function wasGloballyHandled403(response: Response): Promise<boolean> {
    if (response.status !== 403) {
        return false;
    }
    try {
        return sessionEndedCodeFrom(await response.clone().json()) !== null;
    } catch {
        return false;
    }
}

/**
 * Marker set on `Error.cause` once a failure has been handed to the dialog, so the call site
 * that catches it knows the user has already been told and renders nothing of its own.
 *
 * The dialog is modal and non-dismissible, so an inline banner behind it is at best redundant
 * and at worst contradicts it -- "couldn't verify your session" under a dialog offering a
 * reload, which is what the homepage composer showed (ENG-1667). Mirrors WAF_ERROR_CAUSE,
 * which the same catch blocks already consult.
 */
export const SESSION_ENDED_ERROR_CAUSE = "SESSION_ENDED";

/** Whether this failure is already being reported by the dialog. */
export function isSessionEndedError(error: unknown): boolean {
    return error instanceof Error && error.cause === SESSION_ENDED_ERROR_CAUSE;
}

/** Reset between tests. Not part of the runtime flow -- the dialog navigates or reloads instead. */
export function resetSessionEndedState(): void {
    state.value = null;
    pageIsPublic = false;
    // Replaced rather than emptied, since a WeakSet has no clear().
    recoveryDeclined = new WeakSet<Response>();
}
